/**
 * <sizing-modal-opener> Custom Element
 *
 * Wraps a trigger button only. On click of the inner button, fetches the
 * `sizing-modal` section's HTML from the current product URL via the
 * Section Rendering API and pushes it to the notifier as a forced modal
 * (name: "sizing"). The fetched HTML is cached per-pathname for the
 * page's lifetime so repeat opens are zero-cost.
 *
 * Markup contract (rendered by snippets/sizing-modal-opener.liquid):
 *
 *   <sizing-modal-opener>
 *     <button class="show-sizing-button js-show-sizing-button …">…</button>
 *   </sizing-modal-opener>
 *
 * Public methods:
 *   .open() — programmatically open the modal.
 *
 * Naming follows Dawn's <modal-opener> convention; will pair with a
 * <sizing-modal-dialog> when the notifier-based modal flow migrates
 * to native <dialog>.
 *
 * AI: Entirely written by AI
 * Date: 2026-05-09
 * Model: Claude Opus 4.7
 * Updated: 2026-05-25 (Claude Opus 4.7) — moved modal markup off inline
 *   <template> onto Shopify Section Rendering API fetch.
 */

import { log } from './log.js';
import { push as notifierPush } from './notifier.js';
import { fetchSection } from './sections.js';

const INIT_CLASS = 'sizing-modal-opener--init';
const SECTION_ID = 'sizing-modal';

// Per-pathname promise cache. Storing the in-flight promise (not just
// the resolved string) coalesces concurrent open calls during the fetch.
const modalHtmlCache = new Map();

function loadModalHtml() {
  const key = window.location.pathname;
  if (modalHtmlCache.has(key)) return modalHtmlCache.get(key);
  const promise = fetchSection(SECTION_ID, key).catch((err) => {
    modalHtmlCache.delete(key);
    throw err;
  });
  modalHtmlCache.set(key, promise);
  return promise;
}

class SizingModalOpener extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains(INIT_CLASS)) return;
    this.classList.add(INIT_CLASS);

    const trigger = this.querySelector('button');
    if (!trigger) return;
    trigger.addEventListener('click', this._handleClick);
  }

  _handleClick = (event) => {
    event.preventDefault();
    this.open();
  };

  async open() {
    let content;
    try {
      content = await loadModalHtml();
    } catch (err) {
      log('[SizingModalOpener] fetch failed', err);
      return;
    }
    if (!content || !content.trim()) return;
    // PJAX nav may have swapped the page during the fetch. Don't push a
    // stale modal (showing product A's measurements while the user is on
    // product B). Custom elements report isConnected reliably across
    // Turbo's morphing/swap strategies.
    if (!this.isConnected) return;

    // Shopify's Section Rendering API wraps every response in
    // `<div id="shopify-section-{id}" class="shopify-section">…</div>`.
    // Unwrap so the modal body contains only the .sizing-modal markup,
    // keeping the modal-content DOM consistent with the inline-template
    // path that preceded this migration.
    const doc = new DOMParser().parseFromString(content, 'text/html');
    const sectionWrapper = doc.querySelector('#shopify-section-' + SECTION_ID);
    const inner = (sectionWrapper ? sectionWrapper.innerHTML : content).trim();
    if (!inner) return;
    notifierPush({ type: 'modal', content: inner, name: 'sizing', force: true });
  }
}

customElements.define('sizing-modal-opener', SizingModalOpener);
log('[SizingModalOpener] registered');

export { SizingModalOpener };
